import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SectionCard } from './SectionCard';
import { Plus, Trash2, Edit, Save, X } from 'lucide-react';

export default function HeroManager({ onSaved, toast }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(async () => { try { const { data } = await api.get('/admin/hero'); setItems(data); } catch { /* ignore */ } }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing({ image: '', alt: '', sort_order: items.length, active: true }); setShowForm(true); };
  const cancel = () => { setEditing(null); setShowForm(false); };
  const save = async () => {
    if (!editing.image) { toast({ title: 'Image URL required', variant: 'destructive' }); return; }
    try {
      if (editing.id) await api.put(`/admin/hero/${editing.id}`, editing);
      else await api.post('/admin/hero', editing);
      toast({ title: 'Saved' }); cancel(); load(); onSaved && onSaved();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };
  const remove = async (id) => { if (!window.confirm('Delete?')) return; await api.delete(`/admin/hero/${id}`); load(); onSaved && onSaved(); };

  return (
    <SectionCard title="Hero Banner Slides" subtitle={`${items.length} slides`} action={<button data-testid="add-hero-btn" onClick={startNew} className="btn-brand text-sm"><Plus className="w-4 h-4" /> Add Slide</button>}>
      {showForm && (
        <div className="mb-4 p-4 border border-[#f7941e]/30 bg-[#fff7e6] rounded-lg space-y-3">
          <div><Label>Image URL *</Label><Input value={editing.image} onChange={e => setEditing({ ...editing, image: e.target.value })} />
            {editing.image && <img src={editing.image} alt="preview" className="mt-2 max-h-40 rounded border" />}</div>
          <div><Label>Alt Text</Label><Input value={editing.alt} onChange={e => setEditing({ ...editing, alt: e.target.value })} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Sort Order</Label><Input type="number" value={editing.sort_order || 0} onChange={e => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} /></div>
            <label className="flex items-end gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="btn-brand text-sm"><Save className="w-4 h-4" /> Save</button>
            <button onClick={cancel} className="inline-flex items-center gap-1.5 text-sm bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map(it => (
          <div key={it.id} className="border border-gray-200 rounded-lg overflow-hidden">
            <img src={it.image} alt={it.alt} className="w-full aspect-[16/7] object-cover" />
            <div className="p-3 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{it.alt || 'Untitled'}</p>
                <p className="text-[11px] text-gray-500">Order: {it.sort_order} · {it.active ? 'Active' : 'Hidden'}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditing({ ...it }); setShowForm(true); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
                <button onClick={() => remove(it.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
